import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import UserMenu from './UserMenu.js';

function Topbar({ currentUser, logout, cockpitTabs, activeTab, onTabChange }) {
  const [darkMode, setDarkMode] = useState(
    localStorage.getItem('darkMode') === 'true' ||
    window.matchMedia('(prefers-color-scheme: dark)').matches
  );
  const [isIframe, setIsIframe] = useState(false);
  const [isTabsMenuOpen, setIsTabsMenuOpen] = useState(false);
  const navigate = useNavigate();

  // Check if we're in a cockpit context
  const isCockpitContext = Boolean(cockpitTabs);

  // Check if current user is an admin
  const isAdmin = currentUser?.isadmin || currentUser?.issuperadmin;

  // Filter tabs based on admin status
  const visibleTabs = useMemo(() => {
    if (!cockpitTabs) return [];
    return cockpitTabs.filter(tab => !tab.admin_only || isAdmin);
  }, [cockpitTabs, isAdmin]);

  // Map original tab index to visible tab index
  const getOriginalTabIndex = (visibleIndex) => {
    if (!cockpitTabs) return 0;
    return cockpitTabs.findIndex((_, i) => {
      const tabsBeforeVisible = cockpitTabs
        .slice(0, i)
        .filter(tab => tab.admin_only && !isAdmin)
        .length;
      return i - tabsBeforeVisible === visibleIndex;
    });
  };

  useEffect(() => {
    // Detect if we're in an iframe
    setIsIframe(window !== window.parent);

    if (darkMode) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
    localStorage.setItem('darkMode', darkMode);
  }, [darkMode]);

  const toggleDarkMode = () => {
    setDarkMode(!darkMode);
  };

  // If we're in an iframe, show a minimal version
  if (isIframe) {
    return (
      <div className="fixed top-2 right-2 z-50">
        <UserMenu
          currentUser={currentUser}
          logout={logout}
          darkMode={darkMode}
          toggleDarkMode={toggleDarkMode}
        />
      </div>
    );
  }

  // Normal render outside iframe
  return (
    <div className="bg-white dark:bg-gray-800 shadow-md">
      <div className="container mx-auto px-4 py-3 flex justify-between items-center">
        {/* Left side: Logo or Tabs */}
        {isCockpitContext ? (
          <>
            {/* Mobile: Hamburger menu for tabs */}
            <div className="md:hidden">
              <button
                onClick={() => setIsTabsMenuOpen(!isTabsMenuOpen)}
                className="p-2 rounded-md text-gray-600 hover:text-gray-900 dark:text-gray-300 dark:hover:text-white focus:outline-none"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                </svg>
              </button>

              {/* Mobile: Tabs dropdown */}
              {isTabsMenuOpen && (
                <>
                  <div
                    className="fixed inset-0 z-10"
                    onClick={() => setIsTabsMenuOpen(false)}
                  />
                  <div className="absolute left-0 top-full mt-2 w-64 rounded-md shadow-lg bg-white dark:bg-gray-800 ring-1 ring-black ring-opacity-5 z-50">
                    <div className="py-1">
                      {visibleTabs.map((tab, index) => (
                        <button
                          key={index}
                          onClick={() => {
                            onTabChange(getOriginalTabIndex(index));
                            setIsTabsMenuOpen(false);
                          }}
                          className={`block w-full text-left px-4 py-2 text-sm ${
                            activeTab === getOriginalTabIndex(index)
                              ? 'bg-gray-100 dark:bg-gray-700 text-blue-600 dark:text-blue-400'
                              : 'text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700'
                          }`}
                        >
                          <div className="flex items-center">
                            {tab.name}
                            {tab.admin_only && (
                              <span className="ml-2 inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-200">
                                Admin
                              </span>
                            )}
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>
                </>
              )}

              {/* Mobile: Current tab name */}
              <div className="ml-3 text-sm font-medium text-gray-700 dark:text-gray-200">
                {cockpitTabs[activeTab]?.name || 'Onglet'}
              </div>
            </div>

            {/* Desktop: Horizontal tabs */}
            <div className="hidden md:flex overflow-x-auto flex-1">
              {visibleTabs.map((tab, index) => (
                <button
                  key={index}
                  onClick={() => onTabChange(getOriginalTabIndex(index))}
                  className={`px-4 py-1 text-sm font-medium whitespace-nowrap transition-colors duration-200 border-b-2 focus:outline-none ${
                    activeTab === getOriginalTabIndex(index)
                      ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                      : 'border-transparent text-gray-600 hover:text-gray-800 dark:text-gray-300 dark:hover:text-white'
                  }`}
                >
                  <div className="flex items-center">
                    {tab.name}
                    {tab.admin_only && (
                      <span className="ml-1.5 inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-200">
                        Admin
                      </span>
                    )}
                  </div>
                </button>
              ))}
            </div>
          </>
        ) : (
          <div
            className="text-xl font-bold text-gray-800 dark:text-white cursor-pointer"
            onClick={() => navigate('/')}
          >
            Cockpit
          </div>
        )}

        {/* Right side: User menu */}
        <div className="flex items-center space-x-4">
          <UserMenu
            currentUser={currentUser}
            logout={logout}
            darkMode={darkMode}
            toggleDarkMode={toggleDarkMode}
          />
        </div>
      </div>
    </div>
  );
}

export default Topbar;